"use client";

import { useEffect, useRef, useState } from "react";
import Link from "./LocaleLink";
import QuickRequestForm from "./QuickRequestForm";
import { useLocale } from "./LocaleProvider";

const copy = {
  en: {
    imgAlt: "EgoDent Clinic",
    titleTop: "EgoDent Dental & Medical",
    titleBottom: "Where care meets confidence.",
    sub: "Dental & Medical clinics in Stanmore and Walton-on-Thames.",
    book: "Book online",
    request: "Send a request",
    offers: "Explore our offers",
    modalEyebrow: "Quick enquiry",
    modalTitle: "Send your request",
    modalSub: "We’ll get back to you as soon as possible.",
    close: "Close",
  },
  ro: {
    imgAlt: "Clinica EgoDent",
    titleTop: "EgoDent Stomatologie și Medicină",
    titleBottom: "Unde grija întâlnește încrederea.",
    sub: "Clinici stomatologice și medicale în Stanmore și Walton-on-Thames. Personalul și medicii noștri vorbesc limba română.",
    book: "Programare online",
    request: "Trimite o solicitare",
    offers: "Vezi ofertele noastre",
    modalEyebrow: "Solicitare rapidă",
    modalTitle: "Trimiteți solicitarea",
    modalSub: "Vă vom răspunde cât mai curând posibil.",
    close: "Închide",
  },
} as const;

export default function HeroStack() {
  const [open, setOpen] = useState(false);
  const t = copy[useLocale()];
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  // ESC close + iOS-safe body scroll lock when modal open
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);

    // iOS-safe background scroll lock
    const scrollY = window.scrollY;
    document.documentElement.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    // focus scroll area for iOS gesture capture
    setTimeout(() => {
      scrollAreaRef.current?.focus();
    }, 0);

    return () => {
      document.removeEventListener("keydown", onKeyDown);

      const y = Math.abs(parseInt(document.body.style.top || "0", 10)) || 0;
      document.documentElement.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, y);
    };
  }, [open]);

  return (
    <>
      {/* HERO */}
      <section className="relative w-full h-[90vh] overflow-hidden -mt-6 md:-mt-32">
        {/* Background image */}
        <img
          src="/cover.png"
          alt={t.imgAlt}
          className="absolute inset-0 h-full w-full object-cover object-[center_10%]"
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/60" />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h1 className="text-4xl md:text-6xl font-semibold text-white mb-4">
            {t.titleTop}
            <br />
            {t.titleBottom}
          </h1>

          <p className="text-white/80 text-sm md:text-base mb-8">
            {t.sub}
          </p>

          {/* CTA buttons (NO OVERLAP on mobile) */}
          <div className="w-full max-w-sm sm:max-w-none mx-auto flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
            {/* Book online */}
            <Link
              href="/book-online"
              className="
                inline-flex items-center justify-center rounded-full
                bg-[#27E8A7] px-7 py-3
                text-xs font-semibold uppercase tracking-[0.18em] text-black
                hover:brightness-110 transition
                w-full sm:w-auto
              "
            >
              {t.book}
            </Link>

            {/* Send request -> modal */}
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="
                inline-flex items-center justify-center rounded-full
                border border-[#27E8A7]/40 px-7 py-3
                text-xs uppercase tracking-[0.18em] text-white
                hover:border-[#27E8A7] hover:text-[#27E8A7] hover:bg-black/60
                transition
                w-full sm:w-auto
              "
            >
              {t.request}
            </button>

            {/* Offers (separat, NU suprapus) */}
            <Link
              href="/offers"
              className="
                inline-flex items-center justify-center rounded-full
                border border-white/15 bg-white/5 px-7 py-3
                text-xs uppercase tracking-[0.18em] text-white/85
                hover:bg-white/10 hover:text-white transition
                w-full sm:w-auto
              "
            >
              {t.offers}
            </Link>
          </div>
        </div>
      </section>

      {/* MODAL */}
      {open && (
        <div className="fixed inset-0 z-[9999]">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Center wrapper */}
          <div className="relative z-10 flex h-[100dvh] w-full items-center justify-center p-4">
            <div className="w-full max-w-4xl">
              <div className="rounded-3xl border border-[#27E8A7]/20 bg-black/85 shadow-[0_0_60px_rgba(0,0,0,0.75)]">
                {/* Sticky header */}
                <div className="sticky top-0 z-20 rounded-t-3xl border-b border-white/10 bg-black/90 backdrop-blur-md p-4 md:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs tracking-[0.25em] uppercase text-[#27E8A7]/70">
                        {t.modalEyebrow}
                      </p>
                      <h3 className="mt-2 text-2xl md:text-3xl font-semibold text-white">
                        {t.modalTitle}
                      </h3>
                      <p className="mt-2 text-sm text-white/70">
                        {t.modalSub}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="shrink-0 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/80 hover:bg-white/10 transition"
                    >
                      {t.close}
                    </button>
                  </div>
                </div>

                {/* Scroll area */}
                <div
                  ref={scrollAreaRef}
                  tabIndex={-1}
                  className="max-h-[calc(100dvh-140px)] overflow-y-auto overscroll-contain p-4 md:p-6"
                  style={{
                    WebkitOverflowScrolling: "touch",
                    touchAction: "pan-y",
                  }}
                >
                  <QuickRequestForm />
                  <div className="h-6" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
